import { useMemo } from "react"
import { selectUndoable } from "../../helpers/selector.helpers"
import {
    DEFAULT_OBJECT_STYLES,
    DEFAULT_OBJECT_STYLES_WITH_ID,
    ObjectStylesWithId,
    ObjectsById,
    singleSelectionWrapper,
} from "../../helpers/wrapper.helpers"
import { useAppSelector } from "../useRedux"

export { DEFAULT_OBJECT_STYLES, DEFAULT_OBJECT_STYLES_WITH_ID }

const NO_OBJECTS: ObjectsById = {}

/**
 * Single-object wrapper geometry: bounding box of the selected object and its children,
 * in the object's parent coordinate space. Active only for a single selection.
 *
 * @param objectId when given, the geometry is computed only while THAT object is the single
 * selection, and the hook subscribes to a plain string instead of the selection array. Every
 * AbstractContainer calls this hook but only the selected one draws a wrapper — without the
 * id all of them would re-render on every click.
 */
const useWrapSelectedObject = (objectId?: string) => {
    const selectedId = useAppSelector((state) => {
        const selected = state.activeObject.selected
        if (selected.length !== 1) return ""
        if (objectId !== undefined && selected[0] !== objectId) return ""
        return selected[0]
    })
    // Subscribe to the objects map only while this object IS the single selection. Every
    // AbstractContainer calls this hook (twice — directly and through useDraggable), so an
    // unconditional subscription made all ~200 of them re-render on every object change,
    // including every mouse-up at the end of a drag.
    const objectsById = useAppSelector((state) =>
        selectedId ? (selectUndoable(state).objects.value as ObjectsById) : NO_OBJECTS
    )

    return useMemo(() => {
        if (!selectedId) {
            return {
                wrapperObjectsStyles: DEFAULT_OBJECT_STYLES_WITH_ID as ObjectStylesWithId,
                activeChildObjectIds: [] as string[],
            }
        }
        return singleSelectionWrapper(selectedId, objectsById)
    }, [selectedId, objectsById])
}

export default useWrapSelectedObject
